import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Share2, ArrowRight, Moon, Sun, Timer, Activity } from "lucide-react";
import { Section, Eyebrow, Heading, Lede } from "../components/site/Section";
import {
  getDrop, submitGuess, redeemCode, getArchiveStats, getActivity,
  hasGuessedThisWeek, markGuessedThisWeek,
  recordPersonalUnlock, getPersonalCount,
  NATIONS_LIST, NATION_BY_SLUG, RARITY_STYLES,
} from "../lib/archive";
import { getClaimedNation } from "../lib/quiz";
import { useAuth, getMyStreak } from "../lib/auth";
import { Navigate } from "react-router-dom";
import NationSignal from "../components/site/NationSignal";

// ============================================================
// Countdown to a future ISO timestamp — ticks every second.
// ============================================================
function useCountdown(targetIso) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!targetIso) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [targetIso]);
  if (!targetIso) return null;
  const ms = new Date(targetIso).getTime() - now;
  if (ms <= 0) return { mm: "00", ss: "00", expired: true };
  const totalSec = Math.floor(ms / 1000);
  const mm = String(Math.floor(totalSec / 60)).padStart(2, "0");
  const ss = String(totalSec % 60).padStart(2, "0");
  return { mm, ss, expired: false };
}

function formatAgo(iso) {
  if (!iso) return "";
  const sec = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
  return `${Math.floor(sec / 86400)}d ago`;
}

function RarityPill({ rarity, size = "md" }) {
  const s = RARITY_STYLES[rarity] || RARITY_STYLES.C;
  return (
    <span
      className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase ${
        size === "lg" ? "text-xs px-3 py-1.5" : ""
      }`}
      style={{ borderColor: `${s.color}55`, color: s.color, background: s.bg }}
      data-testid={`rarity-${rarity}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: s.color }}
      />
      {s.label}
    </span>
  );
}

function WeeklyDrop({ data, onUnlock, user, streak, onStreakUpdate, personalCount = 0 }) {
  const drop_key = data?.drop_id || data?.week_key;
  const mode = data?.mode || "weekly";
  const phase = data?.phase;
  const isOwner = Boolean(user?.is_owner);
  const expires = mode === "con" ? data?.expires_at : null;
  const countdown = useCountdown(expires);
  const [picked, setPicked] = useState("");
  const [busy, setBusy] = useState(false);
  const [verdict, setVerdict] = useState(null); // "correct" | "wrong" | null
  const [revealed, setRevealed] = useState(null);
  const [alreadyGuessed, setAlreadyGuessed] = useState(false);
  const [huntOpensAt, setHuntOpensAt] = useState(null);
  const huntClosed = data?.hunt_window && data.hunt_window.is_open === false && !isOwner;
  useEffect(() => {
    if (huntClosed) {
      setHuntOpensAt(data.hunt_window.opens_at || null);
    }
  }, [huntClosed, data?.hunt_window?.opens_at]);

  // Sync the gate to localStorage whenever the active drop changes.
  // Owners are exempt from the lockout entirely (they can retry wrong guesses).
  useEffect(() => {
    setAlreadyGuessed(isOwner ? false : hasGuessedThisWeek(drop_key));
    setVerdict(null);
    setRevealed(null);
    setPicked("");
  }, [drop_key, isOwner]);

  if (!data || !data.character) {
    return (
      <div
        className="border border-dashed border-white/10 p-10 text-center bg-[#0A0A0A]"
        data-testid="archive-empty"
      >
        <p className="text-[#A39E99]">No signal this week. The archive is silent.</p>
      </div>
    );
  }

  const status = data.status;
  const ch = data.character;

  // Already unlocked by community — show the reveal
  if (status === "unlocked" || revealed) {
    const r = revealed || ch;
    const rstyle = RARITY_STYLES[r.rarity] || RARITY_STYLES.C;
    const RARITY_BORDER = { C: "#8C5A2B", B: "#B8B8B8", A: "#3F7BD0", S: "#C5A059" };
    const borderClr = RARITY_BORDER[r.rarity] || rstyle.color;
    const nationInfo = NATION_BY_SLUG[r.nation];
    const claimed = getClaimedNation();
    const isYourNation = claimed && claimed === r.nation;
    const justUnlocked = Boolean(revealed); // user just cracked it this session
    const isFirstDiscovery = Boolean(r.first_discovery); // truly new for this user
    const abilities = (r.abilities || "")
      .split(/[•·,;]/)
      .map((a) => a.trim())
      .filter(Boolean);

    const shareText = `I uncovered ${r.name} — ${rstyle.label} record from ${nationInfo?.name || r.nation} — in The Bakhtak Archive. Chains Beneath Bakhtak.`;
    const shareUrl = typeof window !== "undefined" ? `${window.location.origin}/archive` : "";

    const onShare = async () => {
      if (typeof navigator !== "undefined" && navigator.share) {
        try {
          await navigator.share({ title: "The Bakhtak Archive", text: shareText, url: shareUrl });
          return;
        } catch { /* cancelled */ }
      }
      try {
        await navigator.clipboard.writeText(`${shareText} ${shareUrl}`);
        toast.success("Reveal copied. Spread the signal.");
      } catch {
        toast.error("Could not share. Try copying the URL.");
      }
    };

    return (
      <motion.div
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-[#0A0A0A] border-2 relative overflow-hidden"
        style={{ borderColor: `${borderClr}55`, boxShadow: `0 0 80px -30px ${borderClr}` }}
        data-testid="archive-revealed"
      >
        {/* Just-unlocked celebratory banner */}
        {justUnlocked && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-between gap-3 px-5 md:px-8 py-3 border-b"
            style={{
              borderColor: `${borderClr}40`,
              background: `linear-gradient(90deg, ${borderClr}24 0%, transparent 60%)`,
            }}
            data-testid={isFirstDiscovery ? "archive-new-record-banner" : "archive-just-unlocked-banner"}
          >
            <div className="flex items-center gap-2.5">
              <motion.span
                animate={{ rotate: [0, 14, -10, 0], scale: [1, 1.18, 1] }}
                transition={{ duration: 1.4, repeat: 1, ease: "easeInOut" }}
                style={{ color: borderClr }}
                aria-hidden
              >
                <Sparkles size={16} />
              </motion.span>
              <span
                className="text-[10px] md:text-xs tracking-[0.32em] uppercase font-medium"
                style={{ color: borderClr }}
              >
                {isFirstDiscovery ? "New Record Recovered" : "Record Recovered"}
              </span>
            </div>
            <span className="text-[10px] tracking-[0.24em] uppercase text-[#737373] hidden sm:inline">
              + Added to your Cast
            </span>
          </motion.div>
        )}

        <div className="grid md:grid-cols-12">
          <div className="md:col-span-5 aspect-square md:aspect-auto bg-[#050505] relative overflow-hidden">
            {r.image ? (
              <motion.img
                initial={justUnlocked ? { scale: 1.08, opacity: 0 } : false}
                animate={justUnlocked ? { scale: 1, opacity: 1 } : false}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
                src={r.image}
                alt={r.name}
                className="w-full h-full object-cover"
                style={{ objectPosition: r.image_position || "center" }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs">
                ARTWORK PENDING
              </div>
            )}
            <div className="absolute top-3 left-3 flex flex-col gap-2 items-start">
              <RarityPill rarity={r.rarity} size="lg" />
              {isYourNation && (
                <span
                  className="inline-flex items-center gap-1.5 border px-2.5 py-1 text-[10px] tracking-[0.22em] uppercase bg-black/50 backdrop-blur-sm"
                  style={{ borderColor: `${nationInfo?.accent || "#C5A059"}80`, color: nationInfo?.accent || "#C5A059" }}
                  data-testid="archive-your-nation-badge"
                >
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: nationInfo?.accent || "#C5A059" }} />
                  Your Nation
                </span>
              )}
            </div>
          </div>
          <div className="md:col-span-7 p-7 md:p-10">
            <div className="text-[10px] tracking-[0.32em] uppercase" style={{ color: rstyle.color }}>
              {r.title || "Archive Recovered"}
            </div>
            <h3 className="font-display text-3xl md:text-5xl text-[#EAE0D5] mt-2 leading-[1.05]">
              {r.name}
            </h3>
            <div className="text-xs text-[#737373] mt-2 tracking-[0.18em] uppercase">
              {nationInfo?.name || r.nation}
            </div>
            {r.quote && (
              <p className="italic text-[#A39E99] mt-5 leading-relaxed text-base md:text-lg">
                "{r.quote}"
              </p>
            )}
            {r.bio && (
              <p className="text-[#A39E99] mt-4 leading-relaxed text-sm md:text-base">{r.bio}</p>
            )}
            {abilities.length > 0 && (
              <div className="mt-6 pt-5 border-t border-white/5">
                <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059] mb-3">
                  Abilities
                </div>
                <ul className="flex flex-wrap gap-1.5" data-testid="archive-abilities-chips">
                  {abilities.map((a) => (
                    <li
                      key={a}
                      className="text-[11px] tracking-[0.06em] px-2.5 py-1.5 border"
                      style={{
                        borderColor: `${rstyle.color}40`,
                        color: "#D8CFC1",
                        background: `${rstyle.color}0D`,
                      }}
                    >
                      {a}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action row — make the reward feel earned */}
            <div className="mt-7 flex flex-wrap items-center gap-3">
              <Link
                to="/characters"
                data-testid="archive-view-cast-btn"
                className="bg-[#C5A059] text-black px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase hover:bg-[#D4AF68] transition-colors inline-flex items-center gap-2"
              >
                View on the Cast <ArrowRight size={13} />
              </Link>
              <button
                type="button"
                onClick={onShare}
                data-testid="archive-share-unlock-btn"
                className="border border-white/15 text-[#EAE0D5] px-5 py-2.5 text-[11px] tracking-[0.24em] uppercase hover:border-[#C5A059]/50 inline-flex items-center gap-2"
              >
                <Share2 size={13} /> Share the find
              </button>
              {nationInfo && (
                <Link
                  to={`/nations#${nationInfo.slug}`}
                  className="text-[11px] tracking-[0.22em] uppercase hover:underline"
                  style={{ color: nationInfo.accent }}
                  data-testid="archive-nation-link"
                >
                  More from {nationInfo.name} →
                </Link>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Locked: artwork + guess UI
  const onGuess = async () => {
    if (!picked) {
      toast.error("Choose a nation.");
      return;
    }
    setBusy(true);
    try {
      const res = await submitGuess(ch.id, picked);
      // Nightly hunt shutdown — no guess is recorded and the drop is preserved.
      if (res.closed) {
        setVerdict("closed");
        setHuntOpensAt(res.opens_at || null);
        toast.error(
          res.reason || `The scrolls are shut. Return at ${res.opens_at || "11 AM ET"}.`,
          { duration: 8000 }
        );
        if (res.streaks && onStreakUpdate) onStreakUpdate(res.streaks);
        return;
      }
      // Con-mode daily cap hit — server refuses the unlock even on a correct guess.
      // Do NOT mark the drop as consumed so the user can return after the day rolls over.
      if (res.capped) {
        setVerdict("capped");
        toast.error(res.reason || `Daily archive cap of ${res.daily_cap} reached — return tomorrow.`, { duration: 8000 });
        if (res.streaks && onStreakUpdate) onStreakUpdate(res.streaks);
        return;
      }
      markGuessedThisWeek(drop_key);
      setAlreadyGuessed(true);
      if (res.correct) {
        setVerdict("correct");
        setRevealed({ ...res.character, first_discovery: !!res.first_discovery });
        recordPersonalUnlock(res.character);
        onUnlock && onUnlock(res.character);
        toast.success(
          res.first_discovery
            ? `NEW RECORD RECOVERED — ${res.character.name} added to your Cast.`
            : `${res.character.name} is already in your archive.`
        );
      } else {
        setVerdict("wrong");
        const msg = mode === "con"
          ? "Archive Access Denied. Wait for the next signal."
          : "Archive Access Denied. The signal remains sealed.";
        toast.error(msg);
      }
      // Streak-based bonus unlock (auto-granted by the backend on milestones).
      if (res.bonus_unlock?.character) {
        const bc = res.bonus_unlock.character;
        recordPersonalUnlock(bc);
        onUnlock && onUnlock(bc);
        // Use a longer toast so the player notices the bonus reward.
        toast.success(
          `BONUS: ${bc.name} unlocked — ${res.bonus_unlock.reason || ""}`,
          { duration: 8000 }
        );
      }
      // Refresh local streak indicator if the parent passed an updater.
      if (res.streaks && onStreakUpdate) onStreakUpdate(res.streaks);
    } catch (e) {
      if (e?.response?.status === 401) {
        toast.error("Sign in to enter the Archive.");
      } else {
        toast.error("The candle flickered. Try again.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="bg-[#0A0A0A] border border-[#C5A059]/25"
      data-testid="archive-drop"
    >
      <div className="grid md:grid-cols-12">
        <div className="md:col-span-6 aspect-square md:aspect-auto bg-[#050505] relative overflow-hidden">
          {ch.image ? (
            <motion.img
              key={ch.id}
              initial={{ scale: 1.05, opacity: 0.5 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 1.2 }}
              src={ch.image}
              alt="Unknown figure"
              className="w-full h-full object-cover"
              data-testid="archive-locked-image"
              draggable={personalCount >= 30 ? false : undefined}
              onContextMenu={personalCount >= 30 ? (e) => e.preventDefault() : undefined}
              onDragStart={personalCount >= 30 ? (e) => e.preventDefault() : undefined}
              style={{
                filter:
                  "blur(14px) saturate(0.55) brightness(0.55) contrast(1.15)",
                transform: "scale(1.08)",
                ...(personalCount >= 30
                  ? {
                      WebkitTouchCallout: "none",
                      WebkitUserSelect: "none",
                      userSelect: "none",
                      WebkitUserDrag: "none",
                    }
                  : null),
              }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#737373] text-xs">
              SILHOUETTE
            </div>
          )}
          {/* Vignette + crimson tint to camouflage any in-image typography. */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              background:
                "radial-gradient(ellipse at center, rgba(0,0,0,0) 30%, rgba(0,0,0,0.85) 100%), linear-gradient(180deg, rgba(168,50,58,0.10) 0%, rgba(0,0,0,0.4) 100%)",
            }}
          />
          {/* Grain texture overlay */}
          <div
            className="absolute inset-0 pointer-events-none opacity-30 mix-blend-overlay"
            style={{
              backgroundImage:
                "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.55'/></svg>\")",
            }}
          />
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center p-6 md:p-10">
            {ch.quote ? (
              <p
                className="font-display italic text-[#EAE0D5] text-center text-lg md:text-2xl leading-snug max-w-md select-none"
                style={{ textShadow: "0 2px 18px rgba(0,0,0,0.95), 0 0 36px rgba(0,0,0,0.7)" }}
                data-testid="archive-locked-quote"
              >
                "{ch.quote}"
              </p>
            ) : (
              <span className="font-display text-7xl md:text-8xl text-[#C5A059]/15 select-none">?</span>
            )}
          </div>
          <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
            <div className="text-[10px] tracking-[0.32em] uppercase text-[#C5A059] bg-black/60 backdrop-blur-sm px-2.5 py-1 border border-[#C5A059]/30">
              Archive Status: Locked
            </div>
            <div className="text-[10px] tracking-[0.28em] uppercase bg-black/60 backdrop-blur-sm px-2.5 py-1 border border-white/10 text-[#A39E99]">
              Unknown Record · Nation: ???
            </div>
          </div>
          {mode === "con" && (
            <div
              className="absolute top-3 right-3 flex flex-col items-end gap-1.5"
              data-testid="archive-con-overlay"
            >
              {countdown && !countdown.expired && (
                <div
                  className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.2em] uppercase bg-black/70 backdrop-blur-sm px-2.5 py-1 border border-[#C5A059]/40 text-[#C5A059] tabular-nums"
                  data-testid="archive-countdown"
                  title="Time before this signal rotates"
                >
                  <Timer size={12} />
                  {countdown.mm}:{countdown.ss}
                </div>
              )}
              <div
                className="inline-flex items-center gap-1.5 text-[10px] tracking-[0.22em] uppercase bg-black/60 backdrop-blur-sm px-2 py-1 border"
                style={{
                  borderColor: phase === "vigil" ? "#6F4FB580" : "#C5A05980",
                  color: phase === "vigil" ? "#9F7FE0" : "#C5A059",
                }}
                data-testid={`archive-phase-${phase || "day"}`}
              >
                {phase === "vigil" ? <Moon size={11} /> : <Sun size={11} />}
                {phase === "vigil" ? "Night Vigil" : "Floor Open"}
              </div>
            </div>
          )}
        </div>
        <div className="md:col-span-6 p-7 md:p-10 flex flex-col">
          <Eyebrow>{mode === "con" ? "Current Signal" : "This Week's Signal"}</Eyebrow>
          <h3 className="font-display text-2xl md:text-4xl text-[#EAE0D5] mt-3 leading-tight">
            Which nation does this figure belong to?
          </h3>
          <p className="text-sm text-[#A39E99] mt-3 leading-relaxed">
            Identify them correctly to release the record from the vault.{" "}
            {mode === "con"
              ? "One wrong guess seals you out until this signal rotates."
              : "One wrong guess seals the archive until the next signal."}
          </p>

          {/* Streak indicator — only when the user is engaging in personal guesses. */}
          {streak && (streak.correct_streak > 0 || streak.fail_streak > 0 || streak.next_correct || streak.next_fail || streak.con_cap) && (
            <div className="mt-5 flex flex-wrap items-center gap-3 text-[11px] tracking-[0.22em] uppercase" data-testid="archive-streak">
              {streak.con_cap && (
                <span
                  className={`inline-flex items-center gap-1.5 border px-2.5 py-1 ${
                    streak.con_cap.remaining === 0
                      ? "border-[#B85C5C]/40 text-[#B85C5C]"
                      : "border-[#EAE0D5]/30 text-[#EAE0D5]"
                  }`}
                  data-testid="archive-daily-cap"
                >
                  <span aria-hidden>◈</span> Today {streak.con_cap.todays_unlocks}/{streak.con_cap.daily_cap}
                  {streak.con_cap.remaining === 0 && (
                    <span className="normal-case tracking-normal">&nbsp;· cap reached</span>
                  )}
                </span>
              )}
              {streak.correct_streak > 0 && (
                <span className="inline-flex items-center gap-1.5 border border-[#C5A059]/40 text-[#C5A059] px-2.5 py-1" data-testid="streak-correct">
                  <span aria-hidden>◆</span> Streak {streak.correct_streak}
                  {streak.next_correct && (
                    <span className="text-[#A39E99] normal-case tracking-normal">
                      &nbsp;· {streak.next_correct.remaining} from {streak.next_correct.reward_name}
                    </span>
                  )}
                </span>
              )}
              {streak.fail_streak > 0 && (
                <span className="inline-flex items-center gap-1.5 border border-[#B85C5C]/40 text-[#B85C5C] px-2.5 py-1" data-testid="streak-fail">
                  <span aria-hidden>✕</span> Denials {streak.fail_streak}
                  {streak.next_fail && (
                    <span className="text-[#A39E99] normal-case tracking-normal">
                      &nbsp;· {streak.next_fail.remaining} from {streak.next_fail.reward_name}
                    </span>
                  )}
                </span>
              )}
            </div>
          )}

          {(verdict === "closed" || huntClosed) ? (
            <div className="mt-6 border border-[#C5A059]/30 bg-[#0a0806] p-5 text-center" data-testid="archive-hunt-closed">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
                The scrolls are shut
              </div>
              <p className="text-sm text-[#EAE0D5] mt-2">
                The Archive keeps hours.
              </p>
              <p className="text-xs text-[#A39E99] mt-1">
                Return at {huntOpensAt || "11:00 AM ET"} to continue the hunt.
              </p>
            </div>
          ) : verdict === "capped" ? (
            <div className="mt-6 border border-[#B85C5C]/30 bg-[#1a0a0a] p-5 text-center" data-testid="archive-daily-capped">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#B85C5C]">
                Daily cap reached
              </div>
              <p className="text-sm text-[#EAE0D5] mt-2">
                You&apos;ve recovered {25} records today.
              </p>
              <p className="text-xs text-[#A39E99] mt-1">
                The scrolls close at midnight ET — return tomorrow to continue.
              </p>
            </div>
          ) : alreadyGuessed && verdict !== "correct" ? (
            <div className="mt-6 border border-white/10 bg-[#050505] p-5 text-center" data-testid="archive-already-guessed">
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">
                Archive Access Denied
              </div>
              <p className="text-sm text-[#737373] mt-2">
                {mode === "con"
                  ? "You may try again when this signal rotates."
                  : "Return for the next signal."}
              </p>
              {mode === "con" && countdown && !countdown.expired && (
                <p className="mt-3 inline-flex items-center gap-1.5 text-[11px] tracking-[0.24em] uppercase text-[#C5A059] tabular-nums">
                  <Timer size={12} /> Next signal in {countdown.mm}:{countdown.ss}
                </p>
              )}
            </div>
          ) : (
            <>
              <div
                className="mt-6 grid grid-cols-3 gap-2"
                role="radiogroup"
                aria-label="Choose a nation"
                data-testid="archive-nation-choices"
              >
                {NATIONS_LIST.map((n) => (
                  <button
                    key={n.slug}
                    type="button"
                    role="radio"
                    aria-checked={picked === n.slug}
                    onClick={() => setPicked(n.slug)}
                    data-testid={`archive-pick-${n.slug}`}
                    className={`text-[10px] tracking-[0.22em] uppercase py-2.5 px-2 border transition-colors ${
                      picked === n.slug
                        ? "border-[#C5A059] bg-[#C5A059]/15 text-[#C5A059]"
                        : "border-white/10 text-[#A39E99] hover:border-[#C5A059]/40 hover:text-[#EAE0D5]"
                    }`}
                  >
                    {n.name}
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={onGuess}
                disabled={busy || !picked}
                data-testid="archive-submit-guess"
                className="mt-6 self-start bg-[#C5A059] text-black px-7 py-3 text-xs tracking-[0.24em] uppercase hover:bg-[#D4AF68] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
              >
                {busy ? "Reading the Glyph…" : "Submit to the Archive"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Con Mode hero — community progress + personal tally + ticker
// Renders only when mode === "con". Big, animated, social.
// ============================================================
function ConHero({ stats, drop, activity, personalCount }) {
  if (!stats) return null;
  const total = stats.total || 0;
  const unlocked = stats.total_unlocked || 0;
  const pct = total ? Math.round((unlocked / total) * 100) : 0;
  const phase = drop?.phase;
  const isVigil = phase === "vigil";
  const accent = isVigil ? "#9F7FE0" : "#C5A059";
  const tickerItems = (activity?.recent || []).slice(0, 4);

  return (
    <div
      className="bg-[#0A0A0A] border p-7 md:p-10 relative overflow-hidden"
      style={{
        borderColor: `${accent}40`,
        boxShadow: `0 0 80px -30px ${accent}`,
      }}
      data-testid="archive-con-hero"
    >
      {/* Phase + Live tag row */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-7">
        <div
          className="inline-flex items-center gap-2 px-3 py-1.5 border text-[10px] tracking-[0.32em] uppercase"
          style={{ borderColor: `${accent}60`, color: accent, background: `${accent}10` }}
          data-testid="archive-con-phase-pill"
        >
          {isVigil ? <Moon size={12} /> : <Sun size={12} />}
          {isVigil ? "Night Vigil" : "Con Floor — Live"}
        </div>
        <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#A39E99]">
          <span className="relative inline-flex w-2 h-2">
            <span
              className="absolute inline-flex h-full w-full rounded-full opacity-75 animate-ping"
              style={{ background: accent }}
            />
            <span
              className="relative inline-flex rounded-full h-2 w-2"
              style={{ background: accent }}
            />
          </span>
          Live · Anime NYC
        </div>
      </div>

      <div className="grid md:grid-cols-12 gap-8 md:gap-12">
        {/* Community progress */}
        <div className="md:col-span-7">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99] mb-2">
            Community Progress
          </div>
          <div className="flex items-baseline gap-3">
            <div
              className="font-display text-5xl md:text-7xl tabular-nums leading-none"
              style={{ color: accent }}
              data-testid="archive-community-count"
            >
              {unlocked}
            </div>
            <div className="font-display text-2xl md:text-3xl text-[#737373]">
              / {total} recovered
            </div>
          </div>
          <div className="w-full h-[3px] bg-white/5 mt-5 overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${pct}%` }}
              transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="h-full"
              style={{ backgroundColor: accent }}
            />
          </div>
          <p className="text-xs text-[#A39E99] mt-4 max-w-md leading-relaxed">
            Every correct guess in the room moves this counter. Recover every
            record before the con closes to qualify for the trivia round.
          </p>
        </div>

        {/* Personal tally */}
        <div className="md:col-span-5">
          <div className="text-[10px] tracking-[0.32em] uppercase text-[#A39E99] mb-2">
            Your Record
          </div>
          <div className="flex items-baseline gap-3">
            <div
              className="font-display text-5xl md:text-6xl tabular-nums leading-none text-[#EAE0D5]"
              data-testid="archive-personal-count"
            >
              {personalCount}
            </div>
            <div className="font-display text-2xl text-[#737373]">/ {total}</div>
          </div>
          <p className="text-[11px] text-[#737373] mt-3 leading-relaxed max-w-xs">
            Only the records <span className="text-[#EAE0D5]">you personally cracked</span> count toward the $500 contest. Tracked locally on this device.
          </p>
        </div>
      </div>

      {/* Recent activity ticker */}
      {tickerItems.length > 0 && (
        <div className="mt-8 pt-6 border-t border-white/5" data-testid="archive-ticker">
          <div className="inline-flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[#A39E99] mb-3">
            <Activity size={11} /> Recent Recoveries
          </div>
          <ul className="space-y-1.5">
            <AnimatePresence initial={false}>
              {tickerItems.map((it) => {
                const nation = NATION_BY_SLUG[it.nation];
                const rstyle = RARITY_STYLES[it.rarity] || RARITY_STYLES.C;
                return (
                  <motion.li
                    key={it.id}
                    initial={{ opacity: 0, x: -8 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0 }}
                    className="flex items-center gap-3 text-sm"
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full shrink-0"
                      style={{ backgroundColor: rstyle.color }}
                    />
                    <span className="text-[#EAE0D5]">{it.name}</span>
                    <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">
                      {nation?.name || it.nation}
                    </span>
                    <span className="text-[10px] tracking-[0.18em] uppercase text-[#444] ml-auto">
                      {formatAgo(it.unlocked_at)}
                    </span>
                  </motion.li>
                );
              })}
            </AnimatePresence>
          </ul>
        </div>
      )}
    </div>
  );
}

function StatsBlock({ stats }) {
  if (!stats) return null;
  const { nations, rarity, total, total_unlocked } = stats;
  return (
    <div className="grid md:grid-cols-12 gap-6 md:gap-8" data-testid="archive-stats">
      <div className="md:col-span-7 bg-[#0A0A0A] border border-white/5 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059]">
            Nation Discovery
          </div>
          <div className="text-[10px] tracking-[0.24em] uppercase text-[#737373]">
            {total_unlocked} / {total} unlocked
          </div>
        </div>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
          {NATIONS_LIST.map((n) => {
            const s = nations[n.slug] || { unlocked: 0, total: 0 };
            const pct = s.total ? (s.unlocked / s.total) * 100 : 0;
            return (
              <li key={n.slug} data-testid={`stat-nation-${n.slug}`}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-[#EAE0D5]">{n.name}</span>
                  <span className="text-[#737373]">{s.unlocked} / {s.total}</span>
                </div>
                <div className="w-full h-[3px] bg-white/5">
                  <div
                    className="h-full"
                    style={{ width: `${pct}%`, backgroundColor: n.accent }}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="md:col-span-5 bg-[#0A0A0A] border border-white/5 p-6">
        <div className="text-[10px] tracking-[0.28em] uppercase text-[#C5A059] mb-5">
          Rarity
        </div>
        <ul className="space-y-3">
          {["C", "B", "A", "S"].map((r) => {
            const s = rarity[r] || { unlocked: 0, total: 0 };
            const style = RARITY_STYLES[r];
            const pct = s.total ? (s.unlocked / s.total) * 100 : 0;
            return (
              <li key={r} data-testid={`stat-rarity-${r}`}>
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span style={{ color: style.color }} className="tracking-[0.2em] uppercase">{style.label}</span>
                  <span className="text-[#737373]">{s.unlocked} / {s.total}</span>
                </div>
                <div className="w-full h-[3px] bg-white/5">
                  <div className="h-full" style={{ width: `${pct}%`, backgroundColor: style.color }} />
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}

export default function ArchivePage() {
  const { user, loading: authLoading } = useAuth();
  const [drop, setDrop] = useState(null);
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState({ recent: [] });
  const [personalCount, setPersonalCount] = useState(0);
  const [code, setCode] = useState("");
  const [redeemBusy, setRedeemBusy] = useState(false);
  const [redeemed, setRedeemed] = useState(null);
  const [streak, setStreak] = useState(null);

  const refreshStreak = async () => {
    try { setStreak(await getMyStreak()); } catch { /* silent */ }
  };

  const reload = async () => {
    try {
      const [d, s, a] = await Promise.all([getDrop(), getArchiveStats(), getActivity(6)]);
      setDrop(d);
      setStats(s);
      setActivity(a || { recent: [] });
      setPersonalCount(getPersonalCount());
      refreshStreak();
    } catch (e) { /* silent */ }
  };

  useEffect(() => { if (user) reload(); }, [user]);
  useEffect(() => {
    if (!user || drop?.mode !== "con") return;
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [user, drop?.mode]);

  const onRedeem = async (e) => {
    e?.preventDefault?.();
    const v = code.trim();
    if (!v) return;
    setRedeemBusy(true);
    try {
      const res = await redeemCode(v);
      if (res.correct) {
        setRedeemed(res.character);
        setCode("");
        recordPersonalUnlock(res.character);
        setPersonalCount(getPersonalCount());
        toast.success(res.already_unlocked
          ? `${res.character.name} was already recovered.`
          : `Sealed record opened — ${res.character.name} joins the Cast.`);
        reload();
      } else {
        toast.error(res.message || "That code does not match any sealed record.");
      }
    } catch (err) {
      toast.error("Could not redeem this code.");
    } finally {
      setRedeemBusy(false);
    }
  };

  const isConMode = drop?.mode === "con";

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace />;

  return (
    <div data-testid="archive-page">
      <Section testId="archive-hero" className="pt-12 md:pt-24">
        <Eyebrow>The Bakhtak Archive</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl md:text-7xl text-[#EAE0D5] leading-[1] mt-4 max-w-4xl">
          Forgotten records of the{" "}
          <span className="text-[#C5A059] italic font-light">nine nations</span>.
        </h1>
        <Lede>
          {isConMode
            ? "Anime NYC weekend is live. A new record drops every hour — or the moment someone in the room cracks the current one. Recover them all, present at the booth, answer the trivia question right, win $500."
            : "Every seven days, one sealed record surfaces in the archive. Identify the nation that claims them and the figure is recovered — added to the Cast, permanently, for everyone."}
        </Lede>
      </Section>

      {isConMode && (
        <Section testId="archive-con-hero-section" className="pt-0">
          <ConHero
            stats={stats}
            drop={drop}
            activity={activity}
            personalCount={personalCount}
          />
        </Section>
      )}

      <Section testId="archive-this-week" className="pt-0">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-8">
          <span>{isConMode ? "Current Signal" : "This Week's Signal"}</span>
        </div>
        <WeeklyDrop
          data={drop}
          user={user}
          streak={streak}
          personalCount={personalCount}
          onStreakUpdate={(s) => {
            setStreak((prev) => ({
              ...(prev || {}),
              correct_streak: s.correct,
              fail_streak: s.fail,
            }));
            // Re-pull full streak (next-milestone projection) from server.
            refreshStreak();
          }}
          onUnlock={() => {
            setPersonalCount(getPersonalCount());
            reload();
          }}
        />
      </Section>

      <Section testId="archive-nation-signal" className="pt-0">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-8">
          <span>Your Nation Signal</span>
        </div>
        <NationSignal
          user={user}
          onUnlock={() => {
            setPersonalCount(getPersonalCount());
            reload();
          }}
        />
      </Section>

      <Section testId="archive-redeem-section" className="pt-0">
        <div className="ornate-rule text-[10px] tracking-[0.4em] uppercase text-[#C5A059] mb-8">
          <span>Hidden Records · Code Entry</span>
        </div>
        <div className="border border-white/5 bg-[#0A0A0A] p-6 md:p-8 grid md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-7">
            <p className="text-[#A39E99] text-sm md:text-base leading-relaxed">
              Some figures cannot be guessed. Their records are sealed behind
              <span className="text-[#C5A059]"> redemption codes</span> distributed in person, at events,
              or earned through community milestones. If you carry one, enter it below.
            </p>
          </div>
          <form onSubmit={onRedeem} className="md:col-span-5 flex gap-2" data-testid="redeem-form">
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="ENTER-A-CODE"
              data-testid="redeem-input"
              className="flex-1 bg-black/40 border border-white/15 focus:border-[#C5A059] outline-none px-3 py-2.5 text-[#EAE0D5] text-xs tracking-[0.22em] uppercase placeholder:text-[#444]"
              disabled={redeemBusy}
            />
            <button
              type="submit"
              disabled={redeemBusy || !code.trim()}
              data-testid="redeem-submit"
              className="bg-[#C5A059] text-black px-5 py-2.5 text-[10px] tracking-[0.28em] uppercase hover:bg-[#D4AF68] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {redeemBusy ? "…" : "Redeem"}
            </button>
          </form>

          {redeemed && (
            <div
              className="md:col-span-12 mt-3 border-l-2 pl-4 py-2"
              style={{ borderColor: (RARITY_STYLES[redeemed.rarity] || RARITY_STYLES.C).color }}
              data-testid="redeem-revealed"
            >
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-[10px] tracking-[0.32em] uppercase" style={{ color: (RARITY_STYLES[redeemed.rarity] || RARITY_STYLES.C).color }}>
                  {(RARITY_STYLES[redeemed.rarity] || RARITY_STYLES.C).label} record recovered
                </span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">·</span>
                <span className="font-display text-xl text-[#EAE0D5]">{redeemed.name}</span>
                <span className="text-[10px] tracking-[0.22em] uppercase text-[#737373]">{redeemed.title}</span>
              </div>
              <Link to="/characters" className="inline-block mt-2 text-[10px] tracking-[0.22em] uppercase text-[#C5A059] hover:underline">
                View on the Cast →
              </Link>
            </div>
          )}
        </div>
      </Section>

      <Section testId="archive-stats-section" className="pt-0 border-t border-white/5">
        <Eyebrow>Community Discovery</Eyebrow>
        <Heading className="mb-10">The chain remembered.</Heading>
        <StatsBlock stats={stats} />
        <p className="text-xs text-[#737373] mt-6">
          Records are recovered by the community. Once unlocked, every visitor sees them.
        </p>
      </Section>
    </div>
  );
}
